import { DestroyRef, computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, forkJoin, map, Observable, of, Subscription } from 'rxjs';

import { DRONE_TRACKING_OPTIONS } from '../config/drone-tracking-options';
import { normalizeObserverResponse } from '../map/observer-map.helpers';
import { BaseLayerId } from '../models/base-layer.model';
import { DetectedDroneViewModel, toDetectedDroneViewModels } from '../models/detected-drone.model';
import { DetectedDroneListItem, ObserverListItem } from '../models/entity-list-item.model';
import {
  getObserverPhotoKey,
  normalizeObserverPhotoId,
  ObserverPhotoViewModel,
} from '../models/observer-photo.model';
import { ObserverPositionDto } from '../models/observer-position-dto';
import { DetectedDroneApiService } from './detected-drone-api.service';
import { ObserverApiService } from './observer-api.service';
import { ObserverPhotoStoreService } from './observer-photo-store.service';

export type ObserverMapMode = 'live' | 'paused' | 'replay';

@Injectable({
  providedIn: 'root',
})
export class ObserverMapFacadeService {
  private readonly observerApi = inject(ObserverApiService);
  private readonly detectedDroneApi = inject(DetectedDroneApiService);
  private readonly droneOptions = inject(DRONE_TRACKING_OPTIONS);
  private readonly destroyRef = inject(DestroyRef);
  private readonly photoStore = inject(ObserverPhotoStoreService);

  readonly isLoading = signal(false);
  readonly isAutoRefreshEnabled = signal(false);
  readonly isReplayMenuOpen = signal(false);
  readonly isSettingsMenuOpen = signal(false);
  readonly isEntityListOpen = signal(true);
  readonly isReplayActive = signal(false);
  readonly isReplayPaused = signal(false);
  readonly replayDateTimeInput = signal(this.formatDateTimeLocalInput(new Date()));
  readonly replayCurrentDateTime = signal<Date | null>(null);
  readonly replaySpeed = signal(1);
  readonly replaySpeedOptions = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5,6,7,8,9,10] as const;
  readonly refreshIntervalMs = signal(200);
  readonly lastRefresh = signal<Date | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly baseLayer = signal<BaseLayerId>('osm');
  readonly observers = signal<ObserverPositionDto[]>([]);
  readonly selectedObserverPhoto = signal<ObserverPhotoViewModel | null>(null);
  readonly selectedObserverPhotoGallery = signal<ObserverPhotoViewModel[]>([]);
  readonly selectedObserverPhotoIndex = signal(0);
  readonly detectedDrones = signal<DetectedDroneViewModel[]>([]);
  readonly detectedDronesErrorMessage = signal<string | null>(null);
  readonly isDroneLayerVisible = signal(true);
  readonly detectedDroneStaleAfterMs = signal(this.droneOptions.detectedDroneStaleAfterMs);
  readonly detectedDroneLookbackSeconds = signal(this.droneOptions.defaultLookbackSeconds);
  readonly selectedDroneId = signal<string | null>(null);
  private readonly clock = signal(Date.now());
  readonly observerCount = computed(() => this.observers().length);
  readonly detectedDroneCount = computed(() => this.detectedDrones().length);
  readonly activeDetectedDroneCount = computed(() => this.detectedDrones().filter((drone) => !drone.isStale).length);
  readonly mode = computed<ObserverMapMode>(() => {
    if (this.isReplayActive()) {
      return 'replay';
    }

    return this.isAutoRefreshEnabled() ? 'live' : 'paused';
  });
  readonly modeLabel = computed(() => {
    switch (this.mode()) {
      case 'replay':
        return this.isReplayPaused() ? 'Replay paused' : 'Replay';
      case 'live':
        return 'Live';
      case 'paused':
        return 'Paused';
    }
  });
  readonly replayPlaybackLabel = computed(() => (this.isReplayPaused() ? 'Start replay' : 'Pause replay'));
  readonly apiHealthLabel = computed(() =>
    this.errorMessage() || this.detectedDronesErrorMessage() ? 'API issue' : 'API OK',
  );
  readonly lastRefreshAgeLabel = computed(() => {
    const lastRefresh = this.lastRefresh();

    this.clock();

    if (!lastRefresh) {
      return 'Never refreshed';
    }

    const ageMs = Date.now() - lastRefresh.getTime();

    if (ageMs < 1000) {
      return 'Just now';
    }

    if (ageMs < 60000) {
      return `${Math.floor(ageMs / 1000)}s ago`;
    }

    if (ageMs < 3600000) {
      return `${Math.floor(ageMs / 60000)}m ago`;
    }

    return `${Math.floor(ageMs / 3600000)}h ago`;
  });
  readonly selectedObserverPhotoGalleryCount = computed(() => this.selectedObserverPhotoGallery().length);
  readonly selectedObserverPhotoPositionLabel = computed(() => {
    const count = this.selectedObserverPhotoGalleryCount();

    if (count <= 1) {
      return '';
    }

    return `${this.selectedObserverPhotoIndex() + 1} / ${count}`;
  });
  readonly observerListItems = computed<ObserverListItem[]>(() => {
    const photosById = this.photoStore.photosById();

    return this.observers().map((observer) => {
      const photoId = normalizeObserverPhotoId(observer.photoId);

      return {
        key: getObserverPhotoKey(observer),
        observer,
        photo: photoId ? (photosById[photoId] ?? null) : null,
        hasPhoto: photoId !== null,
      };
    });
  });
  readonly droneListItems = computed<DetectedDroneListItem[]>(() => {
    const observers = this.observers();
    const photosById = this.photoStore.photosById();

    return this.detectedDrones()
      .filter((drone) => !drone.isStale)
      .map((drone) => {
        const sourceDeviceIds = new Set(drone.sourceDeviceIds);
        const relatedObservers = observers.filter((observer) => sourceDeviceIds.has(observer.deviceId));
        const photosByPhotoId = new Map<string, ObserverPhotoViewModel>();

        for (const observer of relatedObservers) {
          const photoId = normalizeObserverPhotoId(observer.photoId);

          if (photoId && photosById[photoId]) {
            photosByPhotoId.set(photoId, photosById[photoId]);
          }
        }

        return {
          drone,
          relatedObservers,
          photos: Array.from(photosByPhotoId.values()),
        };
      });
  });
  readonly selectedDrone = computed(() => {
    const selectedId = this.selectedDroneId();

    if (!selectedId) {
      return null;
    }

    return this.detectedDrones().find((drone) => drone.droneId === selectedId) ?? null;
  });

  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private clockIntervalId: ReturnType<typeof setInterval> | null = setInterval(() => this.clock.set(Date.now()), 1000);
  private replayStartDateTime: Date | null = null;
  private replayStartedAtMs: number | null = null;
  private readonly photoPreloadSubscriptions = new Map<string, Subscription>();

  loadObservers(): void {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const requestDateTime = this.getObserverRequestDateTime();

    const observerRequest$ = this.observerApi.getObservers(requestDateTime).pipe(
      catchError((error: unknown) => {
        console.error('Failed to refresh observations.', error);
        this.errorMessage.set('Could not refresh observations.');
        return of(null);
      }),
    );
    const detectedDronesRequest$ = this.getDetectedDrones(requestDateTime).pipe(
      catchError((error: unknown) => {
        console.error('Failed to refresh detected drones.', error);
        this.detectedDronesErrorMessage.set('Could not refresh detected drones.');
        return of(null);
      }),
    );

    forkJoin({
      observerResponse: observerRequest$,
      detectedDrones: detectedDronesRequest$,
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe(({ observerResponse, detectedDrones }) => {
        if (observerResponse) {
          const observers = normalizeObserverResponse(observerResponse);

          this.observers.set(observers);
          this.preloadObserverPhotos(observers);
          this.lastRefresh.set(new Date());
        }

        if (detectedDrones) {
          this.detectedDronesErrorMessage.set(null);
          this.detectedDrones.set(detectedDrones);
        }
      });
  }

  toggleRefreshing(): void {
    if (this.isAutoRefreshEnabled()) {
      this.stopRefreshing();
      return;
    }

    this.startRefreshing();
  }

  toggleReplayMenu(): void {
    this.isReplayMenuOpen.update((isOpen) => {
      const nextIsOpen = !isOpen;

      if (nextIsOpen) {
        this.isSettingsMenuOpen.set(false);
      }

      return nextIsOpen;
    });
  }

  toggleSettingsMenu(): void {
    this.isSettingsMenuOpen.update((isOpen) => {
      const nextIsOpen = !isOpen;

      if (nextIsOpen) {
        this.isReplayMenuOpen.set(false);
      }

      return nextIsOpen;
    });
  }

  closeSettingsMenu(): void {
    this.isSettingsMenuOpen.set(false);
  }

  toggleEntityList(): void {
    this.isEntityListOpen.update((isOpen) => !isOpen);
  }

  closeEntityList(): void {
    this.isEntityListOpen.set(false);
  }

  setReplayDateTimeInput(value: string): void {
    this.replayDateTimeInput.set(value);
  }

  setReplaySpeed(nextSpeed: number): void {
    if (!Number.isFinite(nextSpeed) || nextSpeed < 1 || nextSpeed > 5) {
      return;
    }

    if (this.isReplayActive()) {
      const currentReplayDateTime = this.getObserverRequestDateTime();
      this.replayStartDateTime = currentReplayDateTime;
      this.replayCurrentDateTime.set(currentReplayDateTime);

      if (!this.isReplayPaused()) {
        this.replayStartedAtMs = Date.now();
      }
    }

    this.replaySpeed.set(nextSpeed);
  }

  setDroneLayerVisible(isVisible: boolean): void {
    this.isDroneLayerVisible.set(isVisible);
  }

  setBaseLayer(baseLayer: BaseLayerId): void {
    this.baseLayer.set(baseLayer);
  }

  setDroneStaleAfterMs(value: number): number {
    return this.updateNumericSignal(this.detectedDroneStaleAfterMs, value, 1000, 120000);
  }

  setDroneLookbackSeconds(value: number): number {
    return this.updateNumericSignal(this.detectedDroneLookbackSeconds, value, 1, 3600);
  }

  setRefreshIntervalMs(value: number): number {
    if (!Number.isFinite(value) || value < 1) {
      return this.refreshIntervalMs();
    }

    const normalizedInterval = Math.min(Math.max(Math.round(value), 100), 360000);
    this.refreshIntervalMs.set(normalizedInterval);

    if (this.isAutoRefreshEnabled()) {
      this.scheduleRefreshInterval();
    }

    return normalizedInterval;
  }

  startReplay(): void {
    const replayDateTime = this.parseReplayDateTimeInput();

    if (!replayDateTime) {
      this.errorMessage.set('Choose a valid replay date and time.');
      return;
    }

    this.replayStartDateTime = replayDateTime;
    this.replayStartedAtMs = Date.now();
    this.replayCurrentDateTime.set(replayDateTime);
    this.isReplayActive.set(true);
    this.isReplayPaused.set(false);
    this.isReplayMenuOpen.set(false);
    this.isSettingsMenuOpen.set(false);

    if (!this.isAutoRefreshEnabled()) {
      this.startRefreshing();
      return;
    }

    this.loadObservers();
  }

  toggleReplayPlayback(): void {
    if (!this.isReplayActive()) {
      this.startReplay();
      return;
    }

    if (this.isReplayPaused()) {
      this.resumeReplay();
      return;
    }

    this.pauseReplay();
  }

  pauseReplay(): void {
    if (!this.isReplayActive() || this.isReplayPaused()) {
      return;
    }

    const currentReplayDateTime = this.getObserverRequestDateTime();
    this.replayStartDateTime = currentReplayDateTime;
    this.replayStartedAtMs = null;
    this.replayCurrentDateTime.set(currentReplayDateTime);
    this.isReplayPaused.set(true);
    this.loadObservers();
  }

  resumeReplay(): void {
    if (!this.isReplayActive() || !this.isReplayPaused()) {
      return;
    }

    this.replayStartDateTime = this.replayCurrentDateTime() ?? this.replayStartDateTime ?? new Date();
    this.replayStartedAtMs = Date.now();
    this.isReplayPaused.set(false);
    this.loadObservers();
  }

  stopReplay(): void {
    this.isReplayActive.set(false);
    this.isReplayPaused.set(false);
    this.replayStartDateTime = null;
    this.replayStartedAtMs = null;
    this.replayCurrentDateTime.set(null);
    this.clearDetectedDrones();

    if (this.isAutoRefreshEnabled()) {
      this.loadObservers();
    }
  }

  startRefreshing(): void {
    if (this.isAutoRefreshEnabled()) {
      return;
    }

    this.isAutoRefreshEnabled.set(true);
    this.loadObservers();
    this.scheduleRefreshInterval();
  }

  stopRefreshing(): void {
    this.isAutoRefreshEnabled.set(false);

    if (!this.refreshIntervalId) {
      return;
    }

    clearInterval(this.refreshIntervalId);
    this.refreshIntervalId = null;
  }

  clearSelectedDrone(): void {
    this.selectedDroneId.set(null);
  }

  selectDroneId(droneId: string | null): void {
    this.selectedDroneId.set(droneId);
  }

  closeSelectedObserverPhoto(): void {
    this.selectedObserverPhoto.set(null);
    this.selectedObserverPhotoGallery.set([]);
    this.selectedObserverPhotoIndex.set(0);
  }

  openObserverPhoto(photo: ObserverPhotoViewModel, gallery: ObserverPhotoViewModel[] = [photo]): void {
    const normalizedGallery = this.normalizeObserverPhotoGallery(gallery.length ? gallery : [photo]);
    const selectedIndex = Math.max(
      0,
      normalizedGallery.findIndex((galleryPhoto) => galleryPhoto.photoId === photo.photoId),
    );

    this.selectedObserverPhotoGallery.set(normalizedGallery);
    this.selectedObserverPhotoIndex.set(selectedIndex);
    this.selectedObserverPhoto.set(normalizedGallery[selectedIndex] ?? photo);
  }

  showPreviousObserverPhoto(): void {
    this.showObserverPhotoAtOffset(-1);
  }

  showNextObserverPhoto(): void {
    this.showObserverPhotoAtOffset(1);
  }

  formatSourceIds(sourceRayIds: number[] | string[] | null | undefined): string {
    if (!sourceRayIds?.length) {
      return 'None';
    }

    const maxVisible = 5;
    const lastIds = sourceRayIds.slice(-maxVisible);
    const hiddenCount = sourceRayIds.length - maxVisible;
    const formatted = lastIds.join(', ');

    if (hiddenCount > 0) {
      return `${formatted} and ${hiddenCount} more`;
    }

    return formatted;
  }

  dispose(): void {
    this.stopRefreshing();
    this.clearPhotoPreloads();

    if (this.clockIntervalId) {
      clearInterval(this.clockIntervalId);
      this.clockIntervalId = null;
    }
  }

  private scheduleRefreshInterval(): void {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }

    this.refreshIntervalId = setInterval(() => this.loadObservers(), this.refreshIntervalMs());
  }

  private updateNumericSignal(
    target: WritableSignal<number>,
    value: number,
    minimumValue: number,
    maximumValue: number,
  ): number {
    if (!Number.isFinite(value)) {
      return target();
    }

    const normalizedValue = Math.min(Math.max(Math.round(value), minimumValue), maximumValue);
    target.set(normalizedValue);

    return normalizedValue;
  }

  private getObserverRequestDateTime(): Date {
    if (!this.isReplayActive() || !this.replayStartDateTime) {
      return new Date();
    }

    if (this.isReplayPaused()) {
      return this.replayCurrentDateTime() ?? this.replayStartDateTime;
    }

    if (this.replayStartedAtMs === null) {
      this.replayStartedAtMs = Date.now();
    }

    const elapsedMs = (Date.now() - this.replayStartedAtMs) * this.replaySpeed();
    const replayDateTime = new Date(this.replayStartDateTime.getTime() + elapsedMs);
    this.replayCurrentDateTime.set(replayDateTime);

    return replayDateTime;
  }

  private getDetectedDrones(requestDateTime: Date): Observable<DetectedDroneViewModel[]> {
    const query = this.isReplayActive()
      ? {
          time: requestDateTime,
          lookbackSeconds: this.detectedDroneLookbackSeconds(),
        }
      : {
          lookbackSeconds: this.detectedDroneLookbackSeconds(),
        };
    const now = this.isReplayActive() ? requestDateTime : new Date();
    const staleAfterMs = this.isReplayActive()
      ? Number.POSITIVE_INFINITY
      : this.detectedDroneStaleAfterMs();

    return this.detectedDroneApi
      .getRecentDetectedDrones(query)
      .pipe(map((drones) => toDetectedDroneViewModels(drones, now, staleAfterMs)));
  }

  private clearDetectedDrones(): void {
    this.detectedDrones.set([]);
    this.detectedDronesErrorMessage.set(null);
    this.selectedDroneId.set(null);
  }

  private showObserverPhotoAtOffset(offset: number): void {
    const gallery = this.selectedObserverPhotoGallery();

    if (gallery.length <= 1) {
      return;
    }

    const nextIndex = (this.selectedObserverPhotoIndex() + offset + gallery.length) % gallery.length;
    this.selectedObserverPhotoIndex.set(nextIndex);
    this.selectedObserverPhoto.set(gallery[nextIndex]);
  }

  private normalizeObserverPhotoGallery(photos: ObserverPhotoViewModel[]): ObserverPhotoViewModel[] {
    const photosById = new Map<string, ObserverPhotoViewModel>();

    for (const photo of photos) {
      photosById.set(photo.photoId, photo);
    }

    return Array.from(photosById.values());
  }

  private parseReplayDateTimeInput(): Date | null {
    const value = this.replayDateTimeInput();

    return this.parseDateTimeLocalInput(value);
  }

  private parseDateTimeLocalInput(value: string): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatDateTimeLocalInput(date: Date): string {
    const pad = (value: number): string => String(value).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes(),
    )}:${pad(date.getSeconds())}`;
  }

  private preloadObserverPhotos(observers: ObserverPositionDto[]): void {
    const incomingPhotoIds = new Set<string>();

    for (const observer of observers) {
      const photoId = normalizeObserverPhotoId(observer.photoId);

      if (photoId) {
        incomingPhotoIds.add(photoId);
      }
    }

    for (const [photoId, subscription] of this.photoPreloadSubscriptions) {
      if (!incomingPhotoIds.has(photoId)) {
        subscription.unsubscribe();
        this.photoPreloadSubscriptions.delete(photoId);
      }
    }

    for (const photoId of incomingPhotoIds) {
      if (this.photoPreloadSubscriptions.has(photoId)) {
        continue;
      }

      const subscription = this.photoStore.loadPhoto(photoId).subscribe({
        error: (error: unknown) => {
          console.error('Failed to load observer photo.', error);
        },
        complete: () => {
          this.photoPreloadSubscriptions.delete(photoId);
        },
      });

      this.photoPreloadSubscriptions.set(photoId, subscription);
    }
  }

  private clearPhotoPreloads(): void {
    for (const subscription of this.photoPreloadSubscriptions.values()) {
      subscription.unsubscribe();
    }

    this.photoPreloadSubscriptions.clear();
  }
}
